import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Phase 1.i (П35Р React #185 diagnostic) — root-level error boundary
 * that captures the offending component stack BEFORE React unmounts
 * the tree on uncaught errors like #185 "Maximum update depth
 * exceeded".
 *
 * Without this boundary an uncaught render error tears down the entire
 * React tree (no canvas, no bridge, blank page) and the smoke harness
 * only sees `[pageerror] Minified React error #185` with no component
 * stack — making it impossible to identify which component triggered
 * the loop.
 *
 * The boundary:
 *   1. Catches the error.
 *   2. Stashes `{ message, componentStack, stack }` on
 *      `globalThis.__react_error_capture` so the smoke harness can
 *      pull it via `page.evaluate(() => globalThis.__react_error_capture)`.
 *   3. Renders a minimal `<pre>` fallback with the captured details
 *      so a developer opening the live tab sees the error inline.
 *
 * `getDerivedStateFromError` MUST be a static method per React API;
 * `componentDidCatch` receives the component-stack string.
 *
 * NOTE: when React error #185 fires inside an effect, the boundary
 * MAY not catch it depending on which scheduler tick the loop trips.
 * If the boundary doesn't fire, the smoke harness's existing
 * `pageerror` capture is the next best signal.
 */
interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
  componentStack?: string;
  stack?: string;
}

export class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(err: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      message: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? "";
    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;
    this.setState({ message, componentStack, stack });
    // Stash on global for the Playwright smoke harness to read via
    // `page.evaluate(() => globalThis.__react_error_capture)`.
    (globalThis as { __react_error_capture?: unknown }).__react_error_capture =
      {
        message,
        componentStack,
        stack,
        capturedAt: new Date().toISOString(),
      };
    // Also log to console for DevTools visibility.
    // eslint-disable-next-line no-console
    console.error(
      "[ErrorBoundary] uncaught render error",
      message,
      "\nComponent stack:",
      componentStack,
      "\nJS stack:",
      stack,
    );
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "1rem",
            background: "#220000",
            color: "#ff9999",
            fontFamily: "monospace",
            fontSize: "11px",
            whiteSpace: "pre-wrap",
            overflow: "auto",
            maxHeight: "100vh",
          }}
          data-testid="error-boundary-fallback"
        >
          <strong>React error caught by boundary:</strong>
          {"\n\n"}
          {this.state.message}
          {"\n\n"}
          <strong>Component stack:</strong>
          {"\n"}
          {this.state.componentStack ?? "(unavailable)"}
          {"\n\n"}
          <strong>JS stack:</strong>
          {"\n"}
          {this.state.stack ?? "(unavailable)"}
        </div>
      );
    }
    return this.props.children;
  }
}
